import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getTokenFromAPI } from "@/api/IntegrationsAPI";

const OAuth2Callback: React.FC = () => {
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return; // <-- Prevent double run
    hasRun.current = true;

    const processCallback = async () => {
      try {
        // Get the code and state from URL parameters
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get("code");
        const state = searchParams.get("state");

        if (!code) {
          throw new Error("No authorization code received");
        }

        if (!state) {
          throw new Error("No state parameter received");
        }

        // The state contains integrationId:propertyId
        const [integrationId, propertyId] = state.split(":");

        if (!integrationId || !propertyId) {
          throw new Error("Invalid state parameter format");
        }

        console.log("Processing OAuth2 callback with:", { code, integrationId, propertyId });

        // Call the API to exchange the code for a token and save the integration
        const result = await getTokenFromAPI(integrationId, code, propertyId);
        
        // Determine integration type to display appropriate message
        let integrationName = "OAuth2";
        if (result && result.name) {
          integrationName = result.name;
        }
        
        // Show success toast
        toast({
          title: `${integrationName} Integration Successful`,
          description: `Your ${integrationName} system has been successfully connected.`,
        });

        // Redirect back to the property settings page ONLY on success
        navigate(`/properties/${propertyId}/settings`);
      } catch (err) {
        console.error("Error processing OAuth2 callback:", err);
        setError(err instanceof Error ? err.message : "Failed to connect with OAuth2 system.");
        
        toast({
          title: "Integration Failed",
          description: err instanceof Error ? err.message : "Failed to connect with OAuth2 system.",
          variant: "destructive",
        });
      } finally {
        // Always set loading to false when done, regardless of success or failure
        setLoading(false);
      }
    };

    processCallback();
  }, [location, navigate, toast]);

  if (loading && !error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <h1 className="text-2xl font-bold">Connecting your integration</h1>
        <p className="text-muted-foreground">Please wait while we complete your integration...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h1 className="text-2xl font-bold text-destructive mb-4">Integration Failed</h1>
        <p className="text-muted-foreground mb-6">{error}</p>
        <button 
          className="bg-primary text-primary-foreground px-4 py-2 rounded"
          onClick={() => navigate("/properties")}
        >
          Return to Properties
        </button>
      </div>
    );
  }

  return null;
};

export default OAuth2Callback;
